import * as THREE from "three";
import type { OrbitControls } from "three/addons/controls/OrbitControls.js";
import type { Pano360Manifest, Pano360Node } from "../types.ts";
import { EquirectSource } from "./EquirectSource.ts";
import { PanoramaSession } from "./PanoramaSession.ts";

const MARKER_RADIUS = 0.05;
const MARKER_COLOR = 0xffaa4a;            // orange — distinct from blue pinhole markers
const MARKER_COLOR_HOVER = 0xffc46a;
const MARKER_COLOR_ALIGNED = 0x4aff6a;    // green for aligned nodes

/**
 * 360° Insta360 panorama layer: octahedron markers on the floor plan, click to
 * enter panorama mode. Panorama rendering goes through the shared
 * {@link PanoramaSession} backed by {@link EquirectSource}.
 *
 * Independent of the RTABmap-pinhole {@link import('./StreetView.ts').StreetView}
 * — both can be loaded for the same project, but only one set of markers is
 * shown at a time.
 */
export class Pano360View {
  private manifest: Pano360Manifest | null = null;
  private source: EquirectSource | null = null;
  private session: PanoramaSession | null = null;

  private markersGroup: THREE.Group;
  private markerMeshes = new Map<number, THREE.Mesh>();
  private sharedGeometry: THREE.OctahedronGeometry;
  private sharedMaterial: THREE.MeshBasicMaterial;
  private alignedMaterial: THREE.MeshBasicMaterial;
  private hoverMaterial: THREE.MeshBasicMaterial;
  private raycaster = new THREE.Raycaster();
  private hoveredMarker: THREE.Mesh | null = null;
  private interactive = true;

  onPanoExit: (() => void) | null = null;
  /** Fires after `session.enter()` resolves — first texture is bound, overlay is open. */
  onPanoEnter: ((nodeId: number) => void) | null = null;

  constructor(
    private camera: THREE.PerspectiveCamera,
    private renderer: THREE.WebGLRenderer,
    private scene: THREE.Scene,
    private controls: OrbitControls,
    private planRoot: THREE.Group,
  ) {
    this.sharedGeometry = new THREE.OctahedronGeometry(MARKER_RADIUS);
    this.sharedMaterial = new THREE.MeshBasicMaterial({
      color: MARKER_COLOR, transparent: true, opacity: 0.9, depthTest: false,
    });
    this.alignedMaterial = new THREE.MeshBasicMaterial({
      color: MARKER_COLOR_ALIGNED, transparent: true, opacity: 0.95, depthTest: false,
    });
    this.hoverMaterial = new THREE.MeshBasicMaterial({
      color: MARKER_COLOR_HOVER, transparent: true, opacity: 1, depthTest: false,
    });

    this.markersGroup = new THREE.Group();
    this.markersGroup.renderOrder = 999;
    this.markersGroup.visible = false;
    this.planRoot.add(this.markersGroup);

    const canvas = this.renderer.domElement;
    canvas.addEventListener("mousemove", this._onMouseMove);
    canvas.addEventListener("click", this._onClick, true);
  }

  get loaded(): boolean { return this.manifest !== null; }
  get isPanoActive(): boolean { return this.session?.isActive ?? false; }
  get markersVisible(): boolean { return this.markersGroup.visible; }

  async loadManifest(manifestUrl: string, imageBaseUrl: string, modelOffset: THREE.Vector3): Promise<void> {
    const resp = await fetch(manifestUrl);
    if (!resp.ok) throw new Error(`Failed to load pano360 manifest (HTTP ${resp.status})`);
    this.manifest = await resp.json();
    const nodes = this.manifest!.nodes ?? [];
    const links = this.manifest!.links ?? [];
    console.log(`[Pano360] manifest: ${nodes.length} nodes, ${links.length} links`);

    this.source = new EquirectSource(this.manifest!, imageBaseUrl, modelOffset);
    this.session = new PanoramaSession(
      this.camera, this.renderer, this.scene, this.controls, this.planRoot, this.source,
    );
    this.session.onExit = () => this.onPanoExit?.();

    this._createMarkers();
  }

  setMarkersVisible(v: boolean): void { this.markersGroup.visible = v; }

  /** Open panorama at the closest node to the current orbit camera. */
  async enterPanorama(): Promise<void> {
    if (!this.session) return;
    if (this.session.isActive) return;
    const nodeId = this.session.findNearestToCamera();
    if (nodeId < 0) return;
    await this.session.enter(nodeId);
    this.onPanoEnter?.(nodeId);
  }

  /** Open panorama at a specific node id. */
  async enterPanoramaAtNode(nodeId: number): Promise<void> {
    if (!this.session) return;
    if (this.session.isActive) return;
    await this.session.enter(nodeId);
    this.onPanoEnter?.(nodeId);
  }

  /** When false, marker clicks/hovers are ignored — for hand-off to alignment editor. */
  setInteractive(on: boolean): void {
    this.interactive = on;
  }

  exitPanorama(): void {
    this.session?.exit();
  }

  /** Live-update active panorama sphere yaw (for the alignment slider). */
  setLiveSphereYaw(yaw: number): void {
    this.session?.setLiveSphereYaw(yaw);
  }

  /** ID of the currently-displayed panorama node, or -1 if none. */
  get activeNodeId(): number {
    return this.session?.nodeId ?? -1;
  }

  /** Snapshot of a node's current data (after any in-memory edits). */
  getNodeData(id: number): Pano360Node | null {
    return this.manifest?.nodes.find((n) => n.id === id) ?? null;
  }

  /** Raycast pano360 markers at NDC coords; returns hit node id or -1. */
  pickMarker(ndc: THREE.Vector2, raycaster: THREE.Raycaster): number {
    raycaster.setFromCamera(ndc, this.camera);
    const hits = raycaster.intersectObjects(this.markersGroup.children, false);
    if (!hits.length) return -1;
    return (hits[0].object.userData.pano360NodeId as number | undefined) ?? -1;
  }

  /**
   * Apply an in-memory edit to a node: marker position + alignment colour, the
   * source's positions/yaws maps, and (if the pano session is showing this
   * node) the live sphere rotation.
   *
   * Caller is responsible for persisting the change via the backend PATCH.
   */
  applyNodeUpdate(
    id: number,
    patch: { position?: THREE.Vector3; yaw?: number; aligned?: boolean },
  ): void {
    if (!this.manifest || !this.source) return;
    const node = this.manifest.nodes.find((n) => n.id === id);
    if (!node) return;

    if (patch.position) {
      node.position = { x: patch.position.x, y: patch.position.y, z: patch.position.z };
      this.source.updateNode(id, { position: patch.position });
      const marker = this.markerMeshes.get(id);
      if (marker) marker.position.copy(patch.position);
    }
    if (typeof patch.yaw === "number") {
      node.yaw = patch.yaw;
      this.source.updateNode(id, { yaw: patch.yaw });
      if (this.activeNodeId === id) this.setLiveSphereYaw(patch.yaw);
    }
    if (typeof patch.aligned === "boolean") {
      node.aligned = patch.aligned;
      const marker = this.markerMeshes.get(id);
      if (marker) {
        marker.material = patch.aligned ? this.alignedMaterial : this.sharedMaterial;
      }
    }
  }

  dispose(): void {
    this.session?.dispose();
    this.source?.dispose();
    const c = this.renderer.domElement;
    c.removeEventListener("mousemove", this._onMouseMove);
    c.removeEventListener("click", this._onClick, true);
    this.markersGroup.removeFromParent();
    this.sharedGeometry.dispose();
    this.sharedMaterial.dispose();
    this.alignedMaterial.dispose();
    this.hoverMaterial.dispose();
    this.markerMeshes.clear();
  }

  // ── Markers ───────────────────────────────────────────────────────────────

  private _createMarkers(): void {
    if (!this.source || !this.manifest) return;
    for (const m of this.markerMeshes.values()) m.removeFromParent();
    this.markerMeshes.clear();

    const alignedById = new Map<number, boolean>();
    for (const n of this.manifest.nodes) alignedById.set(n.id, n.aligned !== false);

    for (const node of this.source.getNodes()) {
      const aligned = alignedById.get(node.id) !== false;
      const mesh = new THREE.Mesh(
        this.sharedGeometry,
        aligned ? this.alignedMaterial : this.sharedMaterial,
      );
      mesh.position.copy(node.position);
      mesh.userData.pano360NodeId = node.id;
      mesh.renderOrder = 999;
      this.markersGroup.add(mesh);
      this.markerMeshes.set(node.id, mesh);
    }
    console.log(`[Pano360] created ${this.markerMeshes.size} markers`);
  }

  private _ndcFromEvent(e: MouseEvent): THREE.Vector2 {
    const r = this.renderer.domElement.getBoundingClientRect();
    return new THREE.Vector2(
      ((e.clientX - r.left) / r.width) * 2 - 1,
      -((e.clientY - r.top) / r.height) * 2 + 1,
    );
  }

  private _restoreMarker(m: THREE.Mesh): void {
    const aligned = (this.manifest?.nodes.find(n => n.id === m.userData.pano360NodeId)?.aligned) !== false;
    m.material = aligned ? this.alignedMaterial : this.sharedMaterial;
    m.scale.setScalar(1);
  }

  private _onMouseMove = (e: MouseEvent): void => {
    if (!this.interactive || !this.markersGroup.visible || this.session?.isActive) return;
    const mouse = this._ndcFromEvent(e);
    this.raycaster.setFromCamera(mouse, this.camera);
    const hits = this.raycaster.intersectObjects(this.markersGroup.children, false);

    if (this.hoveredMarker) this._restoreMarker(this.hoveredMarker);
    this.hoveredMarker = null;

    if (hits.length > 0) {
      const m = hits[0].object as THREE.Mesh;
      m.material = this.hoverMaterial;
      m.scale.setScalar(1.4);
      this.hoveredMarker = m;
      this.renderer.domElement.style.cursor = "pointer";
    } else {
      this.renderer.domElement.style.cursor = "";
    }
  };

  private _onClick = (e: MouseEvent): void => {
    if (!this.interactive || !this.markersGroup.visible || this.session?.isActive || !this.session) return;
    const mouse = this._ndcFromEvent(e);
    this.raycaster.setFromCamera(mouse, this.camera);
    const hits = this.raycaster.intersectObjects(this.markersGroup.children, false);
    if (hits.length === 0) return;
    const nodeId = hits[0].object.userData.pano360NodeId as number | undefined;
    if (nodeId === undefined) return;
    e.stopImmediatePropagation();
    void this.session.enter(nodeId).then(() => this.onPanoEnter?.(nodeId));
  };
}
